import React, { FC } from 'react'

import { FormControl, SelectVariants, useTheme } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { TFunction } from 'i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { RelatedFeatureTypeEnum } from './types'

const getOptions = (t: TFunction) => [
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.accountSetup'),
    value: 'Account Setup',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.profile'),
    value: 'Profile',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.dashboard'),
    value: 'Dashboard',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.dailyStats'),
    value: 'Daily Stats',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.tradingLog'),
    value: 'Trading Log',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.reports'),
    value: 'Reports',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.journaling'),
    value: 'Journaling',
  },
  {
    type: RelatedFeatureTypeEnum.Area,
    label: t('contactUs.form.relatedFeatureLabel.other'),
    value: 'Other Feature',
  },
  {
    type: RelatedFeatureTypeEnum.Platform,
    label: t('contactUs.form.relatedFeatureLabel.metatrader4'),
    value: 'Meta Trader 4',
  },
  {
    type: RelatedFeatureTypeEnum.Platform,
    label: t('contactUs.form.relatedFeatureLabel.metatrader5'),
    value: 'Meta Trader 5',
  },
  {
    type: RelatedFeatureTypeEnum.Platform,
    label: t('contactUs.form.relatedFeatureLabel.otherPlatform', 'Other'),
    value: 'Other Platform',
  },
]

export const RelatedFeatureAutomplete: FC<{
  fieldName: string
  label: string | null
  variant?: SelectVariants
}> = ({ fieldName, label, variant = 'standard' }) => {
  const { t } = useTranslation()

  const { spacing } = useTheme()
  const { control } = useFormContext()

  const options = getOptions(t)

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field, fieldState: { error } }) => (
        <FormControl variant={variant} fullWidth>
          <Autocomplete
            {...field}
            disableClearable={false}
            clearOnBlur={false}
            options={options}
            getOptionLabel={(option) => option?.label ?? ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={options.find((opt) => opt.value === field.value) || null}
            onChange={(_, selectedOption) => field.onChange(selectedOption?.value || '')}
            groupBy={(option) => t(`contactUs.form.relatedFeatureType.${option.type}`)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                fullWidth
                variant={variant}
                error={!!error}
                helperText={error ? error.message : null}
                label={label}
                InputLabelProps={{
                  sx: {
                    fontSize: spacing(2),
                    fontWeight: 500,
                    color: (theme) => theme.palette.grey[700],
                    '&.MuiInputLabel-root': {
                      ...(variant === 'standard' && { top: spacing(-2) }),
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  margin: 'none',
                  sx: {
                    fontSize: '16px',
                    fontWeight: 500,
                  },
                  endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
                }}
              />
            )}
            sx={{
              flexDirection: 'row',
              maxWidth: spacing(68.5),
              '& .MuiInput-root .MuiInput-input': {
                height: spacing(5),
              },
              '& .MuiFormLabel-root': {
                top: spacing(1.25),
                fontSize: 16,
                fontWeight: 500,
              },
              '& .MuiInputLabel-animated': {
                top: 0,
              },
            }}
          />
        </FormControl>
      )}
    />
  )
}
